import { useEffect, useMemo } from 'react';
import type { AccommodationsConsoleState } from '@shared/types/Accommodations.types';
import { useAccommodationPlacesByCouple } from './useAccommodationPlacesByCouple';
import { useRegionalHotels } from './useRegionalHotels';
import { useBackgroundPricingRefresh } from './useBackgroundPricingRefresh';

type UseAccommodationsConsoleListingsArgs = Readonly<{
  state: AccommodationsConsoleState;
  variant: 'couple' | 'regional';
  skipCouplePicks?: boolean;
  shouldFilterOutNonJoyRoomBlocks: boolean;
  eventHandle: Maybe<string>;
  onError?: (error: unknown) => void;
  onSuccess?: () => void;
  isInitialLoad: boolean;
}>;

export const useAccommodationsConsoleListings = (args: UseAccommodationsConsoleListingsArgs) => {
  const { state, variant, skipCouplePicks = false, shouldFilterOutNonJoyRoomBlocks, eventHandle, onError, onSuccess, isInitialLoad } = args;

  const isViewingCouplesPicks = variant === 'couple' && !skipCouplePicks;

  const { eventAccommodations, isFetchingEventAccommodations, errorFetchingAccommodationPrices } = useAccommodationPlacesByCouple({
    state,
    eventHandle,
    skip: skipCouplePicks,
    shouldFilterOutNonJoyRoomBlocks,
  });

  const {
    payload,
    cachedListings,
    hasReceivedRegionalData,
    isFetchingListings,
    error: regionalError,
  } = useRegionalHotels({
    state,
    isInitialLoad,
    eventHandle,
  });

  const {
    error: backgroundError,
    listings: backgroundListings,
    isLoading: isBackgroundPricingLoading,
  } = useBackgroundPricingRefresh({
    isFetchingListings,
    hasReceivedRegionalData,
    regionalHotelsPayload: payload,
    locationName: state.searchLocation,
  });

  const hasAnyError = Boolean(regionalError || backgroundError);
  // Only treat primary regional listing errors as API errors that should show error state
  // Background pricing refresh failures are non-critical and shouldn't trigger error UI
  const hasApiError = Boolean(regionalError);
  const areQueriesSettled = !isFetchingListings && !isBackgroundPricingLoading;
  const shouldUseBackgroundListings = areQueriesSettled && !backgroundError && backgroundListings.length > 0;

  const consolidatedListings = useMemo(() => {
    const regionalListings = shouldUseBackgroundListings ? backgroundListings : cachedListings;

    if (skipCouplePicks) {
      return regionalListings;
    }

    const searchScoreByJoyPlaceId = new Map(regionalListings.filter(l => l.joyPlaceId).map(l => [l.joyPlaceId, l.searchScore]));

    const couplePickJoyPlaceIds = new Set<string>(eventAccommodations.filter(x => x.joyPlaceId).map(x => x.joyPlaceId) as string[]);

    const regionalListingsJoyPlaceIds = new Set<string>(regionalListings.filter(l => l.joyPlaceId).map(l => l.joyPlaceId as string));
    const couplesPickToShow = isInitialLoad
      ? eventAccommodations
      : eventAccommodations.filter(accommodation => accommodation.joyPlaceId && regionalListingsJoyPlaceIds.has(accommodation.joyPlaceId));

    return [
      ...couplesPickToShow.map(accommodation => {
        const matchingRegionalListing = regionalListings.find(listing => listing.joyPlaceId === accommodation.joyPlaceId);
        const updates = {
          searchScore: accommodation.joyPlaceId ? searchScoreByJoyPlaceId.get(accommodation.joyPlaceId) : undefined,
          otaProvider: matchingRegionalListing?.otaProvider,
          // Prefer couple pick's own chain (from secondary fetch), fall back to regional listing
          chain: accommodation.chain ?? matchingRegionalListing?.chain ?? null,
        };
        // The dedicated per-place couple's-pick pricing query (useAccommodationPlacesPricing)
        // averages 2.7s and p95 7.6s — long enough that users see a price skeleton on the
        // featured "Couple's Pick" tile while regional tiles already show prices. When the
        // same hotel appears in the regional listings, its pricing has already loaded; surface
        // it immediately and let the per-place query overwrite when it lands. Only safe when
        // both listings are 'standardHotel' (regional results never include roomBlocks).
        if (
          accommodation.type === 'standardHotel' &&
          !accommodation.pricingInfo.values &&
          matchingRegionalListing?.type === 'standardHotel' &&
          matchingRegionalListing.pricingInfo.values
        ) {
          return { ...accommodation, ...updates, pricingInfo: matchingRegionalListing.pricingInfo };
        }
        return { ...accommodation, ...updates };
      }),
      ...regionalListings.filter(listing => !!listing.joyPlaceId && !couplePickJoyPlaceIds.has(listing.joyPlaceId)),
    ];
  }, [eventAccommodations, backgroundListings, cachedListings, skipCouplePicks, isInitialLoad, shouldUseBackgroundListings]);

  // Notify parent of success/error states
  // Using stable callbacks to prevent infinite loops when error persists
  useEffect(() => {
    if (regionalError) {
      onError?.(regionalError);
    } else if (areQueriesSettled && cachedListings.length > 0) {
      onSuccess?.();
    }
  }, [regionalError, areQueriesSettled, cachedListings.length, onError, onSuccess]);

  return {
    cachedListings,
    listings: consolidatedListings,
    hasAccommodationPlacesByCouple: eventAccommodations.length > 0,
    isFetchingListings: isFetchingListings || isFetchingEventAccommodations,
    isFetchingListingPrices: false,
    hasAnyError,
    hasApiError,
    isCombinedLoading: !areQueriesSettled,
    isUsingBackgroundListings: shouldUseBackgroundListings,
    pricingError: isViewingCouplesPicks ? errorFetchingAccommodationPrices : null,
  };
};
